// One row of the shared gazetteer, at /me/places/:placeId.
//
// Every field is directly editable and autosaves -- typed edits ~1.5s after the
// last keystroke or on leaving the field, checkboxes and pin drags on the spot.
// No Save button; the global SaveStatusPill reports progress and errors.
//
// Three things are particular to this record:
//
// - `nameKey` is DERIVED, never typed. It is what every lookup matches, so it
//   has to track `name` exactly; deriveState rewrites it on each keystroke and
//   the row renders read-only.
// - `source` is shown but not editable. It records HOW the position was
//   arrived at ('geocode' | 'user'), and letting someone relabel a geocoder
//   guess as a hand correction would erase the one distinction it draws.
// - A rename onto a name another row already uses WARNS but never blocks.
//   Duplicates are an anticipated state (pickPlace resolves them curated-first,
//   then oldest), and claiming an existing name in order to outrank a geocoded
//   copy is the curation workflow itself. What the user needs is to see that
//   the loser exists.

import { useCallback, useEffect, useMemo, useState, Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { Link, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { CardHeader, Confirm, DataCard, Field, useStatusMessage } from '../../components';
import { RootCrumbProvider } from '../item/RootCrumb';
import PlaceVisitsSection from './PlaceVisitsSection';
import { NewPlaceName, getPlace } from '../../utilities/Database.mjs';
import * as PlaceStore from '../../utilities/places.mjs';
import { normalizePlaceName } from '../../utilities/places.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import { isPlaced } from '../../fleet-shared/TripRoute.mjs';
import { useAutosave } from '../../utilities/useAutosave';
import { useUnsavedChangesGuard } from '../../utilities/useUnsavedChangesGuard';
import { useIsAdmin } from '../../auth/isAdmin';
import {
  FACILITY_FIELDS,
  placeRowFields,
  validatePlace,
} from '../../fleet-shared/PlaceFields.mjs';
import { urlForPlace, urlForPlaces, urlForPlaceVisits } from '../../fleet-shared/Urls.mjs';

const PlaceEditMap = lazyWithReload(() => import('../../components/map/PlaceEditMap'), 'PlaceEditMap');

// The visits section opens item pages, so the trail those pages draw has to
// lead back to where the user actually is. Without this they would say "Items",
// a section this user never passed through.
const PLACES_CRUMB = { name: 'Places', url: urlForPlaces() };

// The number inputs hand back strings; the map wants numbers.
function coordinate(value) {
  const number = Number(value);
  return Number.isFinite(number) && value !== '' && value !== null ? number : null;
}

const Place = () => {
  const { placeId } = useParams();
  const navigate = useNavigate();
  const [StatusMessage, { showText, showError, showLoading, hideStatus }] = useStatusMessage();

  const [place, setPlace] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [duplicates, setDuplicates] = useState([]);

  // Every other row in the gazetteer, for the dots around this one's pin.
  //
  // usePlaceIndex, not loadAllPlaces: the editor's throwing, cache-priming
  // reader is for the LIST page, which has to see its own writes. Neighbours
  // are context -- read through the 10-minute cache, and an empty answer on a
  // failure is exactly right. Its deps are [enabled], so it does not re-read
  // when savePlaceEdit invalidates that cache on every debounce cycle.
  //
  // PlaceVisitsLoader mounts a SECOND usePlaceIndex when the visits section is
  // opened, and that is deliberate rather than an oversight: Refresh there is a
  // remount whose whole purpose is to rebuild the index after a rename, so
  // hoisting this one and passing it down would silently stop Refresh
  // refreshing. Two mounts against one promise cache is the cheaper mistake.
  const placeIndex = usePlaceIndex();
  const otherPlaces = useMemo(
    // byId, not byKey -- byKey is deduped by name through pickPlace, and a
    // duplicate row is precisely what this map is here to show.
    () => [...(placeIndex?.byId?.values?.() || [])]
      .filter(isPlaced)
      .filter((row) => row.id !== placeId),
    [placeIndex, placeId]
  );

  const recordKey = `place:${placeId}`;

  // A curated row is ground truth, and TaskPogoPlaceWrite refuses anyone
  // outside the admin group who tries to change or delete one, or to mark a
  // row curated. Read here so the page stops OFFERING those writes -- the
  // form locks, Delete goes -- rather than let the save fail into the pill.
  // UX only: the Lambda is the enforcement. `isAdmin` is null until the token
  // has been read, and "not yet known" locks like "no": a curated row must not
  // be editable for the instant before the answer lands.
  const isAdmin = useIsAdmin();
  const locked = Boolean(place?.curated) && isAdmin !== true;
  const curatedLocked = isAdmin !== true;

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        showLoading();
        const row = await getPlace(placeId);
        if (!mounted) return;
        setPlace(row);
        hideStatus();
      } catch (err) {
        if (mounted) showError(err);
      }
    }
    load();
    return () => { mounted = false; };
  }, [placeId, hideStatus, showError, showLoading]);

  const {
    formState,
    setFormState,
    fieldErrors,
    flush,
    guardDirty,
  } = useAutosave({
    recordKey,
    original: place,
    fields: placeRowFields.concat(FACILITY_FIELDS),
    // nameKey is never typed. Deriving it HERE rather than in buildPayload
    // keeps the read-only Name Key row tracking the name box live -- and,
    // more importantly, supplying buildPayload would switch off useAutosave's
    // differsFromBaseline skip, so every debounce cycle would write even when
    // nothing changed. The id goes on in `save` instead. Don't "simplify"
    // this back into a buildPayload.
    deriveState: (previous, next) => ({ ...next, nameKey: normalizePlaceName(next?.name) }),
    validate: validatePlace,
    save: (payload) => PlaceStore.savePlaceEdit({ ...payload, id: place.id }),
    onSaved: (saved) => { if (saved) setPlace(saved); },
  });

  const { promptOpen: showLeavePrompt, confirmLeave, cancelLeave, markClean } =
    useUnsavedChangesGuard(guardDirty);

  // The duplicate check is a network round trip, so it can't live in
  // `validate` (which is synchronous) -- and shouldn't, because it never
  // blocks. Suppressed while the name is still the stub's: every new place
  // starts as "New Place", so the second one would always warn about the first.
  const nameKey = formState?.nameKey;
  const isStubName = formState?.name === NewPlaceName;
  useEffect(() => {
    let mounted = true;
    if (!place?.id || !nameKey || isStubName) {
      setDuplicates([]);
      return () => { mounted = false; };
    }
    PlaceStore.findDuplicatePlaces(nameKey, place.id).then((rows) => {
      if (mounted) setDuplicates(rows);
    });
    return () => { mounted = false; };
  }, [nameKey, isStubName, place?.id]);

  const setCoordinates = useCallback((lat, lon) => {
    // A pin drag or a map tap is a discrete gesture -- one dragend at the end
    // of it -- so it saves on the spot rather than riding the typing debounce.
    setFormState((current) => ({ ...current, lat, lon }), { immediate: true });
  }, [setFormState]);

  // The neighbour popup's two links. Navigating place -> place is safe even
  // with an edit still in the debounce: useAutosave flushes when its recordKey
  // changes, not only on unmount.
  const editPlace = useCallback((id) => navigate(urlForPlace(id)), [navigate]);
  const visitPlace = useCallback((id) => navigate(urlForPlaceVisits(id)), [navigate]);

  async function onConfirmDelete() {
    try {
      showText('Deleting...');
      setShowConfirm(false);

      // Cancel any pending or in-flight autosave FIRST -- a late update
      // against the deleted row must neither error nor resurrect it.
      saveQueue.cancel(recordKey);

      await PlaceStore.removePlace(place.id);

      // Deleting discards any held edits, so the navigation below must not be
      // intercepted by the unsaved-changes guard the user just overrode.
      markClean();
      hideStatus();
      navigate(urlForPlaces());
    } catch (err) {
      setShowConfirm(false);
      showError(err);
    }
  }

  const body = formState && (
    <div>
      {locked && (
        <Container className="mb-2">
          <Alert variant="info" className="py-2 mb-0" data-testid="curated-lock">
            This is a curated place. Only an admin can change or delete it.
          </Alert>
        </Container>
      )}

      <div className="container" onBlur={() => flush()}>
        {/* One fieldset per row rather than a `disabled` prop on Field: a
            disabled fieldset switches off every input inside it, checkbox
            included, and Field's readOnly reaches only its text and number
            inputs. Bootstrap's reboot leaves a fieldset with no box of its
            own, so the rows look exactly as they did. */}
        {placeRowFields.map((field, index) => (
          <fieldset
            key={field.key}
            disabled={locked || (field.key === 'curated' && curatedLocked)}
          >
            <Field
              field={field}
              state={formState}
              setState={setFormState}
              setFocus={index === 0 && !locked}
              error={fieldErrors[field.key]}
              // Derived from Name; typing here would just be overwritten.
              readOnly={field.derived}
              note={
                field.key === 'curated' && curatedLocked
                  ? 'Only an admin can mark a place curated.'
                  : field.note
              }
            />
          </fieldset>
        ))}
      </div>

      {duplicates.length > 0 && (
        <Container className="mb-2">
          <Alert variant="warning" className="py-2 mb-0">
            <div>
              {duplicates.length === 1
                ? 'Another place already uses this name.'
                : `${duplicates.length} other places already use this name.`}{' '}
              Lookups return the curated one, or the oldest — the rest are
              invisible to search.
            </div>
            <div className="mt-1 d-flex flex-wrap gap-2">
              {duplicates.map((duplicate) => (
                <Link key={duplicate.id} to={urlForPlace(duplicate.id)} className="small">
                  {duplicate.name}
                  {duplicate.curated ? ' (curated)' : ''}
                </Link>
              ))}
            </div>
          </Alert>
        </Container>
      )}

      {/* A standalone boolean Field per facility would render each name twice
          -- once in the row's label column, once as the checkbox's own label
          -- and inlineWith only pairs them two at a time. One hand-built row
          instead, wrapping over as many lines as the list needs. */}
      <div className="container">
        <div className="d-flex flex-row align-items-start py-2">
          <div className="col-4 d-flex justify-content-end pe-3">Facilities</div>
          <div className="col-8 d-flex flex-wrap gap-3">
            {FACILITY_FIELDS.map((field) => (
              <Form.Check
                key={field.key}
                type="checkbox"
                id={`place-${field.key}`}
                className="mb-0"
                label={field.name}
                checked={formState[field.key] === true}
                disabled={locked}
                onChange={(event) => setFormState(
                  (current) => ({ ...current, [field.key]: event.target.checked }),
                  { immediate: true }
                )}
              />
            ))}
          </div>
        </div>

        <div className="d-flex flex-row align-items-center py-2">
          <div className="col-4 d-flex justify-content-end pe-3">Source</div>
          <div className="col-8 text-body-secondary">
            {place?.source || '—'}
          </div>
        </div>
      </div>

      <Suspense fallback={<Spinner />}>
        <PlaceEditMap
          lat={coordinate(formState.lat)}
          lon={coordinate(formState.lon)}
          onMove={setCoordinates}
          readOnly={locked}
          places={otherPlaces}
          onEditPlace={editPlace}
          onVisitsPlace={visitPlace}
        />
      </Suspense>

      {!locked && (
        <div className="container m-2 d-flex justify-content-center align-items-center gap-2 flex-wrap">
          <Button variant="danger" onClick={() => setShowConfirm(true)}>
            Delete
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <RootCrumbProvider crumb={PLACES_CRUMB}>
      <div className="pb-2">
        <Container className="pt-3 d-flex justify-content-between align-items-center">
          <Link to={urlForPlaces()}>← Places</Link>
          {/* A <Link>, not a hand-rolled anchor: useUnsavedChangesGuard wraps the
              router's push, so leaving here with a FAILED save still prompts. */}
          <Link to={urlForPlaceVisits(placeId)} className="btn btn-sm btn-outline-secondary">
            <i className="fa-solid fa-route me-1" aria-hidden="true"></i>Visits
          </Link>
        </Container>

        <StatusMessage />

        {place && formState && (
          <DataCard header={<CardHeader text={place.name || 'Place'} />} body={body} />
        )}

        {/* What is logged here, one card below the map. It brings its own
            DataCard and does its own read -- and does neither until asked, since
            answering costs a walk of the whole fleet. */}
        {place && <PlaceVisitsSection placeId={placeId} />}

        <Confirm
          show={showConfirm}
          onConfirm={onConfirmDelete}
          onHide={() => setShowConfirm(false)}
          body="Deleting this place removes it for everyone. Are you sure?"
          confirmText="Confirm Delete"
          title="Delete"
        />

        <Confirm
          show={showLeavePrompt}
          onConfirm={confirmLeave}
          onHide={cancelLeave}
          body="Some changes couldn't be saved. Leave and discard them?"
          cancelText="Stay"
          confirmText="Discard Changes"
          title="Unsaved Changes"
        />
      </div>
    </RootCrumbProvider>
  );
};

export default Place;
